/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { OptionTypeBase, Props as SelectProps } from 'react-select';
import { useField } from '@unform/core';

import { Select, Title } from './styles';
import DropdownIndicator from './DropdownIndicator';

interface Props extends Omit<SelectProps<OptionTypeBase>, 'theme'> {
  name: string;
  label?: string;
  disabled?: boolean;
  isRequired?: boolean;
}

const InputSelect: React.FC<Props> = ({
  name,
  disabled,
  isRequired,
  label,
  ...rest
}) => {
  const selectRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const { fieldName, defaultValue, registerField, error } = useField(name);

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current,
      getValue: (ref: any) => {
        if (rest.isMulti) {
          if (!ref.state.value) {
            return [];
          }

          return ref.state.value.map((option: OptionTypeBase) => option.value);
        }
        if (!ref.state.value) {
          return '';
        }

        return ref.state.value.value ?? ref.state.value;
      },
      setValue: (ref: any, value: any) => {
        if (value && rest.isMulti && Array.isArray(value)) {
          const items = ref.props.options.filter((option: any) =>
            value.includes(option.value),
          );
          ref.select.setValue(items);
        } else if (!value) {
          ref.select.clearValue();
        } else {
          const item = ref.props.options.filter(
            (option: any) => option.value === value,
          );
          if (item && item.length > 0) {
            ref.select.setValue(item[0]);
          } else {
            ref.select.clearValue();
          }
        }
      },
    });
  }, [fieldName, registerField, rest.isMulti]);

  return (
    <div>
      {label && (
        <Title>
          <span>{label}</span>
          {isRequired && <strong>*</strong>}
        </Title>
      )}

      <Select
        defaultValue={defaultValue}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        ref={selectRef}
        classNamePrefix="react-select"
        isDisabled={disabled}
        isErrored={!!error}
        isFocused={isFocused}
        aria-haspopup="true"
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator,
        }}
        {...rest}
      />
    </div>
  );
};

export default InputSelect;
